import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

interface PermissionItem {
  code: string;
  name: string;
  hint: string | null;
  dangerous: boolean;
}

interface PermissionModule {
  module: string;
  label: string;
  permissions: PermissionItem[];
}

interface Role {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  builtinReason: string | null;
  permissions: string[];
  userCount: number;
}

const INPUT =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 ' +
  'dark:bg-slate-800 dark:text-slate-100';

/**
 * 角色。
 *
 * ★ 配角色的人是运营主管,不是写代码的。所以这一屏上**没有一个权限点
 *   是以 `order:view_pii` 的样子出现的** —— 每一条都有中文名,
 *   危险的那几个还写着「给了会怎样」。
 *
 *   给他一屏代码名的后果不是「他配得慢」,是**他全选** ——
 *   而全选正是权限体系失效的方式。
 */
export function RoleSettings({ canManage }: { canManage: boolean }) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [catalogue, setCatalogue] = useState<PermissionModule[]>([]);
  const [editing, setEditing] = useState<Role | 'new' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([
        api<Role[]>('/roles'),
        api<PermissionModule[]>('/permissions'),
      ]);
      setRoles(r);
      setCatalogue(p);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(r: Role) {
    if (!confirm(`删除角色「${r.name}」?`)) return;
    try {
      await api(`/roles/${r.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '删不掉');
    }
  }

  const total = catalogue.reduce((n, m) => n + m.permissions.length, 0);

  return (
    <section aria-label="角色">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">角色</h2>
          <p className="mt-1 text-sm text-slate-500">
            一个角色就是一组权限。给人分角色,而不是一个个勾权限
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setEditing('new')}
            className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white"
          >
            新建角色
          </button>
        )}
      </div>

      {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      <div className="space-y-2">
        {roles.map((r) => (
          <div
            key={r.id}
            className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <span className="font-medium text-slate-900 dark:text-slate-100">{r.name}</span>
                <span className="ml-2 font-mono text-xs text-slate-400">{r.code}</span>
                {r.isSystem && (
                  <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500
                                   dark:bg-slate-800">
                    内置
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500">
                {r.userCount} 人 · {r.permissions.length}/{total} 项权限
              </div>
            </div>

            {r.description && (
              <p className="mt-1 text-sm text-slate-500">{r.description}</p>
            )}
            {/*
              ★ 内置角色改不了要**说明白为什么**,而不是把按钮灰掉了事。
                它是所有租户共用的一行 —— 在这儿改一下,别家跟着变。
            */}
            {r.builtinReason && <p className="mt-1 text-xs text-slate-400">{r.builtinReason}</p>}

            {canManage && (
              <div className="mt-2 text-sm">
                <button
                  onClick={() => setEditing(r)}
                  className="text-brand-600 hover:underline dark:text-brand-500"
                >
                  {r.isSystem ? '查看权限' : '编辑'}
                </button>
                {!r.isSystem && (
                  <button
                    onClick={() => void remove(r)}
                    className="ml-3 text-red-600 hover:underline"
                  >
                    删除
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <RoleDialog
          existing={editing === 'new' ? undefined : editing}
          catalogue={catalogue}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </section>
  );
}

function RoleDialog({
  existing,
  catalogue,
  onClose,
  onSaved,
}: {
  existing?: Role;
  catalogue: PermissionModule[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const readOnly = existing?.isSystem ?? false;
  const [f, setF] = useState({
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    description: existing?.description ?? '',
  });
  const [picked, setPicked] = useState<Set<string>>(new Set(existing?.permissions ?? []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggle(code: string) {
    if (readOnly) return;
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  }

  async function save() {
    if (busy || readOnly) return;
    setBusy(true);
    setErr(null);
    try {
      if (existing) {
        await api(`/roles/${existing.id}`, {
          method: 'PATCH',
          body: {
            name: f.name,
            description: f.description || null,
            permissions: [...picked],
          },
        });
      } else {
        await api('/roles', {
          method: 'POST',
          body: {
            code: f.code.trim().toUpperCase(),
            name: f.name,
            description: f.description || undefined,
            permissions: [...picked],
          },
        });
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="角色"
        className="max-h-[90dvh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5
                   dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {existing ? (readOnly ? `${existing.name}(内置,只能看)` : `编辑 ${existing.name}`) : '新建角色'}
        </h3>
        {readOnly && existing?.builtinReason && (
          <p className="mb-3 text-sm text-slate-500">{existing.builtinReason}</p>
        )}

        <div className="space-y-3">
          {!existing && (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="text-slate-500">代码</span>
                <span className="block text-xs text-slate-400">大写字母,例:WAREHOUSE</span>
                <input
                  aria-label="角色代码"
                  value={f.code}
                  onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
                  className={`${INPUT} font-mono`}
                />
              </label>
              <label className="block text-sm">
                <span className="text-slate-500">名称</span>
                <span className="block text-xs text-slate-400">界面上显示的那个</span>
                <input
                  aria-label="角色名称"
                  value={f.name}
                  onChange={(e) => setF({ ...f, name: e.target.value })}
                  className={INPUT}
                />
              </label>
            </div>
          )}
          {existing && !readOnly && (
            <label className="block text-sm">
              <span className="text-slate-500">名称</span>
              <input
                aria-label="角色名称"
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
                className={INPUT}
              />
            </label>
          )}

          {!readOnly && (
            <label className="block text-sm">
              <span className="text-slate-500">说明(可不填)</span>
              <input
                aria-label="角色说明"
                value={f.description}
                onChange={(e) => setF({ ...f, description: e.target.value })}
                className={INPUT}
              />
            </label>
          )}

          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-sm text-slate-500">权限</span>
              <span className="text-xs text-slate-400">已选 {picked.size} 项</span>
            </div>

            <div className="space-y-3">
              {catalogue.map((m) => {
                const allOn = m.permissions.every((p) => picked.has(p.code));
                return (
                  <div
                    key={m.module}
                    className="rounded-lg border border-slate-200 p-2 dark:border-slate-700"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                        {m.label}
                      </span>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() =>
                            setPicked((prev) => {
                              const next = new Set(prev);
                              for (const p of m.permissions) {
                                allOn ? next.delete(p.code) : next.add(p.code);
                              }
                              return next;
                            })
                          }
                          className="text-xs text-brand-600 hover:underline dark:text-brand-500"
                        >
                          {allOn ? '全不选' : '全选'}
                        </button>
                      )}
                    </div>

                    <div className="grid gap-1 sm:grid-cols-2">
                      {m.permissions.map((p) => (
                        <label
                          key={p.code}
                          className="flex items-start gap-2 rounded px-1 py-1 text-sm
                                     hover:bg-slate-50 dark:hover:bg-slate-800/60"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={picked.has(p.code)}
                            disabled={readOnly}
                            onChange={() => toggle(p.code)}
                            aria-label={p.name}
                          />
                          <span>
                            <span
                              className={
                                p.dangerous
                                  ? 'text-red-700 dark:text-red-400'
                                  : 'text-slate-800 dark:text-slate-200'
                              }
                            >
                              {p.name}
                            </span>
                            {/*
                              ★ 「给了会怎样」直接摆出来,不藏在问号提示里。
                                「订单-导出」听起来像个报表功能,而它导出来的文件里
                                带着几百个收件人的姓名、电话和门牌号,还会被转发。
                            */}
                            {p.hint && (
                              <span className="block text-xs text-slate-400">{p.hint}</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
            {readOnly ? '关闭' : '取消'}
          </button>
          {!readOnly && (
            <button
              onClick={() => void save()}
              disabled={busy || !f.name || (!existing && !f.code)}
              className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white
                         disabled:opacity-40"
            >
              {busy ? '保存中…' : '保存'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
